import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {tap} from 'rxjs/operators';
import {LOGSEVERITY, LOGTYPE} from '@core/app.costants';
import {ForcePasswordService} from '../../force-password/force-password.service';
import {EditPasswordService} from '../../edit-password/edit-password.service';
import {UsersService} from '../users.service';
import {ErrorService} from '@services/error.service';
import {NotificationService} from '@services/notification.service';
import {WatchdogService} from '@services/watchdog.service';
import {IStoreState} from '@core/store/store.state';
import {
  AuthLogoutAction,
  UserAddAction,
  UserAddedAction,
  UserDeleteAction,
  UserDeletedAction,
  UserDeletingErrorAction,
  UserErrorAddingAction,
  UserErrorLoadingAction,
  UserErrorSendindNotificationAction,
  UserErrorUpdatingAction,
  UserErrorUpdatingPasswordAction,
  UserLoadAction,
  UserLoadedAction,
  UserNotifiedPasswordChangeAction,
  UserNotifyPasswordChangeAction,
  UserPasswordIrenYouUpdateAction,
  UserPasswordUpdateAction,
  UserUpdateAction,
  UserUpdatedAction
} from './user.actions';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private usersHttpService: UsersService,
    private editPasswordHttpService: EditPasswordService,
    private sendPasswordChangeNotificationService: ForcePasswordService,
    private notificationService: NotificationService,
    private usersService:UsersService,
    private router: Router,
    private watchdog: WatchdogService,
    private store: Store<IStoreState>,
    private errorService: ErrorService
  ) { }

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserLoadAction),
      tap(() =>
        this.usersHttpService.loadUsers().subscribe({
          next: value => {
            this.store.dispatch(UserLoadedAction({ users: value }));
          }, error: err => {
            this.store.dispatch(UserErrorLoadingAction())
          }
        })
      )
    ), { dispatch: false }
  );

  addUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAddAction),
      tap((action) =>
        this.usersHttpService.addUser(action.user).subscribe({
          next: value => {
            this.store.dispatch(UserAddedAction({ user: value }));

            this.watchdog.writeWatchdog({
              type: LOGTYPE.INFO,
              severity: LOGSEVERITY.NONE,
              message: `E' stato aggiunto l'utente ${action.user.email}`,
            });

            this.router.navigate(['/private-area', 'users', 'list']);
          }, error: err => {
            this.watchdog.writeWatchdog({
              type: LOGTYPE.ERROR,
              severity: LOGSEVERITY.HIGHT,
              message: `Si è verificato un errore durante la creazione dell'utente ${action.user.email}`,
            });
            this.errorService.setError(`Si è verificato un errore durante la creazione dell'utente ${action.user.email}`);
            this.store.dispatch(UserErrorAddingAction());
            this.usersService.isLoading$.next(false);
          }
        })
      )
    ), { dispatch: false }
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDeleteAction),
      tap((action) =>
        this.usersHttpService.deleteUser(action.user).subscribe({
          next: value => {
            this.store.dispatch(UserDeletedAction({ user: value }));

            this.watchdog.writeWatchdog({
              type: LOGTYPE.INFO,
              severity: LOGSEVERITY.NONE,
              message: `E' stato eliminato l'utente ${value.email}`,
            });

            this.router.navigate(['/private-area', 'users', 'list']);

          }, error: err => {
              this.watchdog.writeWatchdog({
                type: LOGTYPE.ERROR,
                severity: LOGSEVERITY.HIGHT,
                message: `Si è verificato un problema durante l'eliminazione dell'utente ${action.user.email}`,
              });
              this.store.dispatch(UserDeletingErrorAction());
          }
        }
        )
      )
    ), { dispatch: false }
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserUpdateAction),
      tap((action) =>
        this.usersHttpService.updateUser({ user: action.user }).subscribe({
          next: value => {
            this.store.dispatch(UserUpdatedAction({ user: value }));

            this.watchdog.writeWatchdog({
              type: LOGTYPE.INFO,
              severity: LOGSEVERITY.NONE,
              message: `L'utente ${action.user.email} è stato correttamente aggiornato`,
            });

            this.router.navigate(['/private-area', 'users']);
          }, error: err => {
              this.watchdog.writeWatchdog({
                type: LOGTYPE.ERROR,
                severity: LOGSEVERITY.HIGHT,
                message: `Si è verificato un problema durante l'aggiornamento dell'utente ${action.user.email}`,
              });
              this.store.dispatch(UserErrorUpdatingAction());
          }
        }
        )
      )
    ), { dispatch: false }
  );

  UserPasswordUpdateAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPasswordUpdateAction),
      tap((action) =>
        this.editPasswordHttpService.updatePassword(action.user).subscribe({
          next: response => {
            this.editPasswordHttpService.checkPasswordErrors(response.status.codice, this.store, this.watchdog, this.router, '/private-area/home',action.user);

            this.editPasswordHttpService.isLoading$.next(false);
          }, error: err => {
            this.watchdog.writeWatchdog({
              type: LOGTYPE.ERROR,
              severity: LOGSEVERITY.HIGHT,
              message: `Si è verificato un problema, riprovare più tardi.`,
            });
            this.errorService.setError(`Si è verificato un problema, riprovare più tardi.`);
            this.store.dispatch(UserErrorUpdatingPasswordAction());
            this.editPasswordHttpService.isLoading$.next(false);
          }
        })
      )
    ), { dispatch: false }
  );

  UserPasswordIrenYouUpdateAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPasswordIrenYouUpdateAction),
      tap((action) =>
        this.editPasswordHttpService.updatePasswordWeb(action.user).subscribe({
          next: response => {
            this.editPasswordHttpService.checkPasswordErrors(response.status.codice, this.store, this.watchdog, this.router, '/private-area/search-customers');

            this.editPasswordHttpService.isLoading$.next(false);
          }, error: err => {
              this.watchdog.writeWatchdog({
                type: LOGTYPE.ERROR,
                severity: LOGSEVERITY.HIGHT,
                message: `Si è verificato un problema, riprovare più tardi.`,
              });
              this.errorService.setError(`Si è verificato un problema, riprovare più tardi.`);
              this.store.dispatch(UserErrorUpdatingPasswordAction());
              this.editPasswordHttpService.isLoading$.next(false);
          }
        })
      )
    ), { dispatch: false }
  );

  notifyPasswordChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserNotifyPasswordChangeAction),
      tap((action) =>
        this.sendPasswordChangeNotificationService.sendPasswordChangeNotfification({ user: action.user })
          .subscribe({
            next: value => {
              this.store.dispatch(UserNotifiedPasswordChangeAction({ status: value }));

              this.watchdog.writeWatchdog({
                type: LOGTYPE.INFO,
                severity: LOGSEVERITY.NONE,
                message: `Invio della Notifica di aggiornamento password a: ${action.user.email}`,
              });
              this.sendPasswordChangeNotificationService.isLoading$.next(false);

            }, error: err => {
              this.watchdog.writeWatchdog({
                type: LOGTYPE.ERROR,
                severity: LOGSEVERITY.HIGHT,
                message: `Si è verificato un problema durante l'invio della notifica a: ${action.user.email}.`,
              });
              this.errorService.setError(
                `Si è verificato un problema durante l'invio della notifica a: ${action.user.email}.`
              );
              this.sendPasswordChangeNotificationService.isLoading$.next(false);
              this.store.dispatch(UserErrorSendindNotificationAction());
            }
          })
      )
    ), { dispatch: false }
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthLogoutAction),
        tap(() => {
          localStorage.clear();
          this.router.navigateByUrl('/login');
        })
      ),
    { dispatch: false }
  );
}
